import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useState, useEffect } from 'react';
import equals from 'ramda/src/equals';
import pathOr from 'ramda/src/pathOr';
import usePrevious from '../../hooks/usePrevious';
import { classNames, reverseSortDirection } from '../../utils';
import Button from '../Button';
import PageSelectControl from '../PageSelectControl';
import { IoIosArrowRoundUp, IoIosArrowRoundDown, IoMdCheckmark, } from 'react-icons/io';
import styles from './CardList.module.css';
const IsExpanded = React.createContext(false);
const cardListRef = React.createRef();
const ExpandIcon = ({ isExpanded, disabled, onClick, expandIconFloatToTopRight, }) => {
    const topRightIconRotation = isExpanded
        ? styles.svgRotMinus90
        : styles.svgRot90;
    const leftIconRotation = isExpanded ? styles.svgRot90 : styles.svgRot0;
    const rotationClassName = expandIconFloatToTopRight
        ? topRightIconRotation
        : leftIconRotation;
    return (_jsx("button", Object.assign({ title: "expand", className: classNames(styles.expandIcon, disabled ? styles.disabledExpandIcon : '', expandIconFloatToTopRight ? styles.expandIconFloatToTopRight : ''), onClick: !disabled ? onClick : undefined, "aria-expanded": isExpanded === true }, { children: _jsx("svg", Object.assign({ width: "12", height: "12", viewBox: "0 0 12 12", className: rotationClassName }, { children: _jsx("path", { d: "M5.6138357 8.85425l-5.4542975-5c-.2127176-.196-.2127176-.512 0-.708.2138085-.195.55852-.195.7723285 0L6 7.79325l5.0681333-4.647c.2138084-.195.55852-.195.7723285 0 .2127176.196.2127176.512 0 .708l-5.4542975 5c-.1069043.097-.2465343.146-.3861643.146s-.27926-.049-.3861643-.146z", fill: disabled ? 'var(--medium-base)' : 'currentColor', transform: "rotate(-90 6 6.000125)" }, void 0) }), void 0) }), void 0));
};
export const Cell = ({ width = '1', className, cellAlign, render, verticallyCenterContent, children, }) => {
    const isExpanded = useContext(IsExpanded);
    return (_jsx("div", Object.assign({ className: classNames(cellAlign ? styles['cell-align-' + cellAlign] : '', verticallyCenterContent ? styles.verticalCenter : '', className), style: {
            gridColumn: `auto / span ${width}`,
        } }, { children: render ? render({ isExpanded }) : children }), void 0));
};
export const HeaderCell = ({ dataIsLoading, isSortable, isActive, sortDirection, onClickSort, width = '1', cellAlign, className, children, }) => {
    let sortIcons = [];
    if (isSortable) {
        if (!isActive) {
            sortIcons = [
                //@ts-ignore
                _jsx(IoIosArrowRoundUp, { className: styles.sortArrow, size: 16 }, "up"),
                //@ts-ignore
                _jsx(IoIosArrowRoundDown, { className: styles.sortArrow, size: 16 }, "down"),
            ];
        }
        else if (sortDirection === 'asc') {
            sortIcons = [
                //@ts-ignore
                _jsx(IoIosArrowRoundDown, { className: styles.sortArrow, size: 16 }, "down"),
            ];
        }
        else {
            sortIcons = [
                //@ts-ignore
                _jsx(IoIosArrowRoundUp, { className: styles.sortArrow, size: 16 }, "up"),
            ];
        }
    }
    return (_jsx("div", Object.assign({ className: classNames(cellAlign ? styles['cell-align-' + cellAlign] : '', className), style: {
            gridColumn: `auto / span ${width}`,
        }, onClick: () => {
            if (!dataIsLoading && isSortable && !!onClickSort) {
                onClickSort();
            }
        } }, { children: _jsxs("div", Object.assign({ className: classNames(styles.headerContainer, isSortable ? styles.clickable : '') }, { children: [_jsx("div", Object.assign({ className: styles.headerTextContainer }, { children: children }), void 0), isSortable && (_jsxs("div", Object.assign({ className: styles.sortIconBar }, { children: [_jsx("span", Object.assign({ className: styles.sortIcon }, { children: sortIcons[0] }), void 0), _jsx("span", Object.assign({ className: styles.sortIcon }, { children: sortIcons[1] }), void 0)] }), void 0))] }), void 0) }), void 0));
};
const ListRowContent = ({ hasExtraContent, expandable, content, numberOfGridColumns, onExpandIconClick, isExpanded, isDisabled, isChecked, noLeftIconColumn, expandIconFloatToTopRight, }) => {
    const renderLeftIcon = () => {
        if (hasExtraContent) {
            return (_jsx(ExpandIcon, { onClick: onExpandIconClick, isExpanded: isExpanded, disabled: isDisabled, expandIconFloatToTopRight: expandIconFloatToTopRight }, void 0));
        }
        else if (isChecked) {
            return _jsx(IoMdCheckmark, { className: styles.sortArrow, size: 16 }, void 0);
        }
        else if (noLeftIconColumn) {
            return null;
        }
        else {
            return _jsx("div", {}, void 0);
        }
    };
    return (_jsxs("div", Object.assign({ className: styles.listRowContent, style: {
            position: 'relative',
            display: 'grid',
            gridGap: '0 0.5rem',
            gridTemplateColumns: `1.5rem repeat(${numberOfGridColumns}, 1fr)`,
        } }, { children: [renderLeftIcon(), typeof content === 'function' ? content({ isExpanded }) : content] }), void 0));
};
function CardListRow({ rowData, expandable, featureCardId, resetFeatureCard, numberOfGridColumns, noLeftIconColumn, backgroundColor, }) {
    const [isExpanded, expand] = React.useState(false);
    const ref = React.useRef(null);
    const prevFeatureCardId = usePrevious(featureCardId);
    const { key, content, status = 'default', extraContent, isDisabled, isChecked, startExpanded, expandIconFloatToTopRight, } = rowData;
    const isFeatureCard = key === String(featureCardId);
    useEffect(() => {
        if (startExpanded) {
            return expand(true);
        }
    }, [startExpanded]);
    useEffect(() => {
        if (expandable && isFeatureCard && !isExpanded) {
            return expand(true);
        }
    }, [expandable, isFeatureCard, isExpanded]);
    useEffect(() => {
        if (!isFeatureCard && prevFeatureCardId !== featureCardId && isExpanded) {
            return expand(false);
        }
    }, [featureCardId, isFeatureCard, prevFeatureCardId, isExpanded]);
    useEffect(() => {
        if (ref.current && isFeatureCard) {
            ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [ref, isFeatureCard]);
    const onExpandIconClick = () => {
        if (isFeatureCard && resetFeatureCard) {
            resetFeatureCard();
        }
        expand(!isExpanded);
    };
    return (_jsx(IsExpanded.Provider, Object.assign({ value: isExpanded }, { children: _jsxs("div", Object.assign({ ref: ref, className: classNames(styles.card, styles['card-status-' + status], isDisabled ? styles.disabled : '', isFeatureCard ? styles.featureCard : ''), style: { backgroundColor }, role: "row" }, { children: [_jsx(ListRowContent, { hasExtraContent: !!extraContent, expandable: expandable, isExpanded: isExpanded, isDisabled: isDisabled, isChecked: isChecked, noLeftIconColumn: noLeftIconColumn, expandIconFloatToTopRight: expandIconFloatToTopRight, content: content, numberOfGridColumns: numberOfGridColumns, onExpandIconClick: onExpandIconClick }, void 0), expandable &&
                    isExpanded &&
                    !isDisabled &&
                    extraContent !== undefined &&
                    extraContent.length > 0
                    ? extraContent.map((c, index) => (_jsx(React.Fragment, { children: _jsx(ListRowContent, { hasExtraContent: false, expandable: expandable, content: c, isExpanded: isExpanded, noLeftIconColumn: noLeftIconColumn, numberOfGridColumns: numberOfGridColumns }, void 0) }, `extraContent${index}`)))
                    : null] }), void 0) }), void 0));
}
const CardList = ({ dataIsLoading, data = [], renderLimit = 0, header, headerClassName = '', featureCardId = -1, resetFeatureCard, numberOfGridColumns = 12, usePageSelectControl, pageSelectTopRef, noDefaultSortColumn, noLeftIconColumn = false, groupHeaders, backgroundColor, setPagination, currentPageFromPaginationHook, }) => {
    const [renderIndex, setRenderIndex] = useState(renderLimit);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortDirection, setSortDirection] = useState(() => {
        const defaultSortColumnIndex = findDefaultSortColumnIndex(header, noDefaultSortColumn);
        return pathOr('asc', [defaultSortColumnIndex, 'defaultSortDirection'], header);
    });
    const [activeSortColumnIndex, setActiveSortColumnIndex] = useState(() => {
        return findDefaultSortColumnIndex(header, noDefaultSortColumn);
    });
    //*** useEffect ***///
    //reset page selector to 1 when data is updated
    const prevData = usePrevious(data);
    useEffect(() => {
        if (prevData && data) {
            //using only key b/c other properties are always unequal
            //on every render (perhaps the ReactNode?)
            const prevDataKeys = [...prevData].map((d) => d.key);
            const currentDataKeys = [...data].map((d) => d.key);
            if (!equals(prevDataKeys, currentDataKeys)) {
                setCurrentPage(1);
            }
        }
    }, [prevData, data]);
    //reset page selector to 1 when user clicks on a header to sort
    useEffect(() => {
        setCurrentPage(1);
    }, [activeSortColumnIndex, sortDirection]);
    //*** SORTING/DATA FUNCTIONS ***///
    const setSortColumn = (columnIndex, defaultSortDirection) => {
        const newSortDirection = activeSortColumnIndex === columnIndex
            ? reverseSortDirection(sortDirection)
            : defaultSortDirection || 'asc';
        setActiveSortColumnIndex(columnIndex);
        setSortDirection(newSortDirection);
    };
    const sortedData = () => {
        if (activeSortColumnIndex === -1)
            return data;
        const headerColumn = header === null || header === void 0 ? void 0 : header[activeSortColumnIndex];
        if (!headerColumn)
            return data;
        const comparator = headerColumn.comparator;
        const sortedData = data.slice();
        if (comparator) {
            sortedData.sort((a, b) => {
                return comparator(a.dataObject || {}, b.dataObject || {}, sortDirection);
            });
        }
        return sortedData;
    };
    const limitData = (data) => {
        const usingLoadMoreButton = !usePageSelectControl && currentPageFromPaginationHook && currentPageFromPaginationHook < 0;
        const areThereItemsLeftToRender = renderIndex !== 0 && renderIndex < data.length;
        const usingPageSelectControl = usePageSelectControl && renderLimit !== undefined;
        if (usingLoadMoreButton && areThereItemsLeftToRender) {
            return data.slice(0, renderIndex);
        }
        else if (usingPageSelectControl) {
            const startIndex = (currentPage - 1) * renderLimit;
            const endIndex = currentPage * renderLimit;
            return data.slice(startIndex, endIndex);
        }
        else if (currentPageFromPaginationHook) {
            const startIndex = (currentPageFromPaginationHook - 1) * renderLimit;
            const endIndex = currentPageFromPaginationHook * renderLimit;
            return data.slice(startIndex, endIndex);
        }
        return data;
    };
    //*** END SORTING/DATA FUNCTIONS ***///
    const dataToDisplay = limitData(sortedData());
    const cardsHaveLeftBorderColor = data.some((d) => !!d.status);
    const expandable = data.some((d) => !!d.extraContent);
    const headerStyle = {
        borderLeft: cardsHaveLeftBorderColor
            ? 'solid 0.1875rem transparent'
            : 'none',
        display: 'grid',
        gridGap: '0 0.5rem',
        alignItems: 'center',
        gridTemplateColumns: `1.5rem repeat(${numberOfGridColumns}, 1fr)`,
    };
    return (_jsxs("div", Object.assign({ role: "table", ref: cardListRef }, { children: [groupHeaders && (_jsxs("div", Object.assign({ role: "row", style: headerStyle, className: `${styles.groupHeader} ${styles.listHeader} ${headerClassName}` }, { children: [!noLeftIconColumn && _jsx("div", {}, void 0), groupHeaders.map(({ className, width, content }) => (_jsx("div", Object.assign({ className: className, style: {
                            gridColumn: `auto / span ${width}`,
                        } }, { children: _jsx("div", Object.assign({ className: content ? styles.groupHeaderText : '' }, { children: content }), void 0) }), void 0)))] }), void 0)), header && (_jsxs("div", Object.assign({ role: "row", style: headerStyle, className: `${styles.listHeader} ${headerClassName}` }, { children: [!noLeftIconColumn && _jsx("div", {}, void 0), header.map(({ cellClassName, width, content, comparator, align, defaultSortDirection, }, columnIndex) => (_jsx(React.Fragment, { children: _jsx(HeaderCell, Object.assign({ isSortable: !!comparator, isActive: activeSortColumnIndex === columnIndex, highlightIsActive: false, sortDirection: sortDirection, onClickSort: !!comparator
                                ? () => setSortColumn(columnIndex, defaultSortDirection)
                                : undefined, width: width, cellAlign: align, className: cellClassName, dataIsLoading: dataIsLoading }, { children: content }), void 0) }, `headerCell${columnIndex}`)))] }), void 0)), dataToDisplay.map((d) => (_jsx(CardListRow, { rowData: d, expandable: expandable, featureCardId: featureCardId, resetFeatureCard: resetFeatureCard, numberOfGridColumns: numberOfGridColumns, noLeftIconColumn: d.noLeftIconColumn || noLeftIconColumn, backgroundColor: backgroundColor }, d.key))), !dataIsLoading &&
                !usePageSelectControl &&
                currentPageFromPaginationHook === undefined &&
                renderIndex !== 0 &&
                renderIndex < data.length && (_jsx("div", Object.assign({ className: styles.loadMore }, { children: _jsx(Button, Object.assign({ variant: "outlined", onClick: () => setRenderIndex(renderIndex + renderLimit) }, { children: `Load more (${data.length - renderIndex})` }), void 0) }), void 0)), !dataIsLoading &&
                usePageSelectControl &&
                data.length > 0 &&
                renderLimit > 0 && (_jsx("div", Object.assign({ className: styles.loadMore }, { children: _jsx(PageSelectControl, { pageCount: Math.ceil(data.length / renderLimit), currentPage: currentPage, setCurrentPage: (currentPage) => {
                        var _a, _b;
                        setCurrentPage(currentPage);
                        if ((_a = pageSelectTopRef === null || pageSelectTopRef === void 0 ? void 0 : pageSelectTopRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) {
                            pageSelectTopRef.current.scrollIntoView();
                        }
                        else if ((_b = cardListRef === null || cardListRef === void 0 ? void 0 : cardListRef.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView) {
                            cardListRef.current.scrollIntoView();
                        }
                    } }, void 0) }), void 0)), !dataIsLoading &&
                renderLimit > 0 &&
                setPagination &&
                data.length > 0 &&
                currentPageFromPaginationHook !== undefined && (_jsx("div", Object.assign({ className: styles.loadMore }, { children: _jsx(PageSelectControl, { pageCount: Math.ceil(data.length / renderLimit), currentPage: currentPageFromPaginationHook, setCurrentPage: (currentPage) => {
                        var _a, _b;
                        setPagination(currentPage, renderLimit);
                        setCurrentPage(currentPage);
                        if ((_a = pageSelectTopRef === null || pageSelectTopRef === void 0 ? void 0 : pageSelectTopRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) {
                            pageSelectTopRef.current.scrollIntoView();
                        }
                        else if ((_b = cardListRef === null || cardListRef === void 0 ? void 0 : cardListRef.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView) {
                            cardListRef.current.scrollIntoView();
                        }
                    } }, void 0) }), void 0))] }), void 0));
};
const findDefaultSortColumnIndex = (header, noDefaultSortColumn) => {
    if (!header || noDefaultSortColumn)
        return -1;
    let defaultSortColumnIndex = header.findIndex(({ isDefaultSortColumn }) => {
        return isDefaultSortColumn;
    });
    if (defaultSortColumnIndex === -1) {
        defaultSortColumnIndex = header.findIndex(({ comparator }) => {
            return !!comparator;
        });
    }
    return defaultSortColumnIndex;
};
export default CardList;
